import { Priority } from "@services/common/types";
import { LocationSchedule } from "@services/locations/types";

export enum SettingsPropertyGroup {
  MonitoringSchedule = "monitoring_schedule",
  MonitoringExpiration = "monitoring_thresholds_expiration",
  MonitoringThresholds = "monitoring_thresholds_ticket",
  Scheduling = "scheduling",
  Recording = "recording",
  General = "general",
  Elog = "elog",
  Support = "support",
}
export type MonitoringScheduleValue = {
  recurrenceRule: string;
  from: string | null;
  to: string | null;
};

export enum Operation {
  More = "More",
  Less = "Less",
  Equal = "Equal",
  NotEqual = "Not Equal",
  In = "In",
  NotIn = "Not In",
  Contains = "Contains",
  NotContains = "Does Not Contain",
  NotNull = "Any Value",
  Null = "No Value",
}
export type ExpirationThreshold = {
  active: boolean;
  operation: Operation;
  event: string;
  dataType: "java.lang.Integer";
  propertyName: string;
  /**
   * These three properties are string representation of a number
   */
  critical: string;
  major: string;
  minor: string;
};

export interface ValueSettingsObject extends ExpirationThreshold {
  clearPropertyName: string;
  issueType: string;
}

export enum BooleanSettings {
  "blockMonitoring" = "blockMonitoring",
  "pauseMonitoringIndefinitely" = "pauseMonitoringIndefinitely",
  "isAutoCloseAgingTickets" = "isAutoCloseAgingTickets",
  "isAutoCloseCleanUpTickets" = "isAutoCloseCleanUpTickets",
  "isAutoCloseTickets" = "isAutoCloseTickets",
  "includeMeetingSetupTime" = "includeMeetingSetupTime",
  "vipMeetingKeepAlive" = "vipMeetingKeepAlive",
  "enableAutoRetry" = "enableAutoRetry",
  "emailMeetingAddUpdate" = "emailMeetingAddUpdate",
  "emailMeetingCancelDelete" = "emailMeetingCancelDelete",
  "sendToRoomContacts" = "sendToRoomContacts",
  "includeIcsInvite" = "includeIcsInvite",
  "vipEmailAlerts" = "vipEmailAlerts",
  "viewPublicRecordingAndEvent" = "viewPublicRecordingAndEvent",
  "viewUnlistedRecordingAndEvent" = "viewUnlistedRecordingAndEvent",
  "enableAttendanceTracking" = "enableAttendanceTracking",
  "showBillingTab" = "showBillingTab",
  "AV Device Module Down Ticket Creation" = "AV Device Module Down Ticket Creation",
}

export enum StringSettings {
  "extraRecipients" = "extraRecipients",
  "vipEmailRecipients" = "vipEmailRecipients",
  "ratingText" = "ratingText",
  "customerAdminGuide" = "customerAdminGuide",
  "enableDeviceSecurity" = "enableDeviceSecurity",
  "enableRoomContactDetails" = "enableRoomContactDetails",
  "encryptionKeyTimeToLiveInDays" = "encryptionKeyTimeToLiveInDays",
  "genericLogo" = "genericLogo",
  "genericLogoPath" = "genericLogoPath",
  "genericTemplatePath" = "genericTemplatePath",
  "maxUserInactivityPeriod" = "maxUserInactivityPeriod",
  "lockUserDuration" = "lockUserDuration",
  "mailboxUserSymphony" = "mailboxUserSymphony",
  "mailboxUserVmr" = "mailboxUserVmr",
  "messageSubjectMassImport" = "messageSubjectMassImport",
  "messageSubjectSymphony" = "messageSubjectSymphony",
  "messageSubjectVmr" = "messageSubjectVmr",
  "portalUrl" = "portalUrl",
  "registrationCompleteContacts" = "registrationCompleteContacts",
  "registrationPortalUrl" = "registrationPortalUrl",
  "supportEmail" = "supportEmail",
  "supportEmailSymphony" = "supportEmailSymphony",
  "symphonyDeploymentLocation" = "symphonyDeploymentLocation",
  "symphonyLogo" = "symphonyLogo",
  "userGuide" = "userGuide",
  "vnocContact" = "vnocContact",
}

export enum IntegerSettings {
  "ticketCommunicationGracePeriodInSecond" = "ticketCommunicationGracePeriodInSecond",
  "TicketGracePeriodInSecond" = "TicketGracePeriodInSecond",
  "roomCertificationValidity" = "roomCertificationValidity",
  "autoCloseAge" = "autoCloseAge",
  "autoCloseCalls" = "autoCloseCalls",
  "autoCloseCleanCalls" = "autoCloseCleanCalls",
  "autoCloseCleanHours" = "autoCloseCleanHours",
  "autoCloseHours" = "autoCloseHours",
  "setupTimeInMinute" = "setupTimeInMinute",
  "autoRetryAttemptTimes" = "autoRetryAttemptTimes",
  "autoRetryAttemptIntervalInMinutes" = "autoRetryAttemptIntervalInMinutes",
  "vipEmailAlertTimeInMins" = "vipEmailAlertTimeInMins",
  "lockUserAttemptsCount" = "lockUserAttemptsCount",
  "monitoringRate" = "monitoringRate",
}
export enum UniquelyTypedSettings {
  "monitoring_schedules" = "monitoring schedules",
  "pauseMonitoring" = "pauseMonitoring",
  "autoCloseTicketPriority" = "autoCloseTicketPriority",
  "staleMonitoringMinutes" = "staleMonitoringMinutes",
  "supportPhones" = "supportPhones",
  "monitoringTriggersSchedule" = "Monitoring Triggers Schedule",
}
export enum ThresholdSettings {
  "deviceWarranty" = "deviceWarranty",
  "deviceLease" = "deviceLease",
  "roomServiceContract" = "roomServiceContract",
}

export interface SettingsProperty<
  G extends SettingsPropertyGroup = SettingsPropertyGroup,
  N = string,
  T = any
> {
  id: number | null;
  name: N;
  group: G;
  type: string;

  deleted: boolean;
  hidden: boolean;
  overriddenAllowed: boolean;

  value: T;
  parentValue: T | null;
}

export type GenericSettingsResponse<
  T extends string,
  G extends SettingsPropertyGroup
> = {
  [k in T]: k extends UniquelyTypedSettings.monitoring_schedules
    ? SettingsProperty<G, k, MonitoringScheduleValue[]>
    : k extends UniquelyTypedSettings.monitoringTriggersSchedule
    ? SettingsProperty<G, k, LocationSchedule>
    : k extends UniquelyTypedSettings.autoCloseTicketPriority
    ? SettingsProperty<G, k, Priority>
    : k extends ThresholdSettings
    ? SettingsProperty<G, k, ExpirationThreshold>
    : k extends BooleanSettings
    ? SettingsProperty<G, k, boolean>
    : k extends IntegerSettings
    ? SettingsProperty<G, k, number>
    : k extends StringSettings
    ? SettingsProperty<G, k, string>
    : k extends UniquelyTypedSettings.supportPhones
    ? SettingsProperty<G, k, string[]>
    : k extends UniquelyTypedSettings.staleMonitoringMinutes
    ? SettingsProperty<G, k, ValueSettingsObject>
    : SettingsProperty<G, k>;
};

const genKeyObject = <T extends string[]>(keys: T): { [k in T[number]]: k } => {
  let temp: any = {};
  keys.forEach((key) => (temp[key] = key));
  return temp;
};
export const MonitoringSchedulePropertyKeys = genKeyObject([
  UniquelyTypedSettings.monitoringTriggersSchedule,
  BooleanSettings.blockMonitoring,
  UniquelyTypedSettings.monitoring_schedules,
  UniquelyTypedSettings.pauseMonitoring,
  BooleanSettings.pauseMonitoringIndefinitely,
  IntegerSettings.ticketCommunicationGracePeriodInSecond,
  IntegerSettings.TicketGracePeriodInSecond,
  BooleanSettings["AV Device Module Down Ticket Creation"],
  IntegerSettings.monitoringRate,
]);

export type MonitoringScheduleSettings = GenericSettingsResponse<
  keyof typeof MonitoringSchedulePropertyKeys,
  SettingsPropertyGroup.MonitoringSchedule
>;

export const MonitoringExpirationPropertyKeys = genKeyObject([
  ThresholdSettings.deviceWarranty,
  ThresholdSettings.roomServiceContract,
  ThresholdSettings.deviceLease,
  IntegerSettings.roomCertificationValidity,
]);

export type MonitoringExpirationSettings = GenericSettingsResponse<
  keyof typeof MonitoringExpirationPropertyKeys,
  SettingsPropertyGroup.MonitoringExpiration
>;

export const MonitoringThresholdsTicketPropertyKeys = genKeyObject([
  IntegerSettings.autoCloseAge,
  IntegerSettings.autoCloseCalls,
  IntegerSettings.autoCloseCleanCalls,
  IntegerSettings.autoCloseCleanHours,
  IntegerSettings.autoCloseHours,
  UniquelyTypedSettings.autoCloseTicketPriority,
  BooleanSettings.isAutoCloseAgingTickets,
  BooleanSettings.isAutoCloseCleanUpTickets,
  BooleanSettings.isAutoCloseTickets,
]);

export type MonitoringThresholdsTicketSettings = GenericSettingsResponse<
  keyof typeof MonitoringThresholdsTicketPropertyKeys,
  SettingsPropertyGroup.MonitoringThresholds
>;

export const SchedulingPropertyKeys = genKeyObject([
  BooleanSettings.includeMeetingSetupTime,
  IntegerSettings.setupTimeInMinute,
  BooleanSettings.vipMeetingKeepAlive,
  BooleanSettings.enableAutoRetry,
  IntegerSettings.autoRetryAttemptTimes,
  IntegerSettings.autoRetryAttemptIntervalInMinutes,
  BooleanSettings.emailMeetingAddUpdate,
  BooleanSettings.emailMeetingCancelDelete,
  BooleanSettings.sendToRoomContacts,
  BooleanSettings.includeIcsInvite,
  StringSettings.extraRecipients,
  BooleanSettings.vipEmailAlerts,
  StringSettings.vipEmailRecipients,
  IntegerSettings.vipEmailAlertTimeInMins,
  StringSettings.ratingText,
]);
export type SchedulingSettings = GenericSettingsResponse<
  keyof typeof SchedulingPropertyKeys,
  SettingsPropertyGroup.Scheduling
>;

export const RecordingSettingsPropertyKeys = genKeyObject([
  BooleanSettings.viewPublicRecordingAndEvent,
  BooleanSettings.viewUnlistedRecordingAndEvent,
]);

export type RecordingSettings = GenericSettingsResponse<
  keyof typeof RecordingSettingsPropertyKeys,
  SettingsPropertyGroup.Recording
>;

export const GeneralPropertyKeys = genKeyObject([
  StringSettings.customerAdminGuide,
  StringSettings.enableDeviceSecurity,
  StringSettings.enableRoomContactDetails,
  StringSettings.encryptionKeyTimeToLiveInDays,
  StringSettings.genericLogo,
  StringSettings.genericLogoPath,
  StringSettings.genericTemplatePath,
  IntegerSettings.lockUserAttemptsCount,
  StringSettings.lockUserDuration,
  StringSettings.maxUserInactivityPeriod,
  StringSettings.mailboxUserSymphony,
  StringSettings.mailboxUserVmr,
  StringSettings.messageSubjectMassImport,
  StringSettings.messageSubjectSymphony,
  StringSettings.messageSubjectVmr,
  StringSettings.portalUrl,
  StringSettings.registrationCompleteContacts,
  StringSettings.registrationPortalUrl,
  StringSettings.supportEmail,
  StringSettings.supportEmailSymphony,
  StringSettings.symphonyDeploymentLocation,
  StringSettings.symphonyLogo,
  StringSettings.userGuide,
  StringSettings.vnocContact,
  UniquelyTypedSettings.staleMonitoringMinutes,
  UniquelyTypedSettings.supportPhones,
]);

export type GeneralSettings = GenericSettingsResponse<
  keyof typeof GeneralPropertyKeys,
  SettingsPropertyGroup.General
>;

export const ElogPropertyKeys = genKeyObject([
  BooleanSettings.enableAttendanceTracking,
  BooleanSettings.showBillingTab,
]);

export type ElogSettings = GenericSettingsResponse<
  keyof typeof ElogPropertyKeys,
  SettingsPropertyGroup.Elog
>;

export interface SupportSettingsItem {
  category: string;
  subText: string;
  linkText: string;
  url: string;
}

export interface SupportSettings {
  "support information": {
    id: number;
    group: string;
    name: string;
    type: string;
    hidden: boolean;
    overriddenAllowed: boolean;
    deleted: false;
    targetId: string;
    level: string;
    value: SupportSettingsItem[];
  };
}
